import {
  accountSearchColumns,
  boPhanSearchColumns,
  chiPhiSearchColumns,
  dotThanhToanSearchColumns,
  hopDongSearchColumns,
  khachHangSearchColumns,
  kheUocSearchColumns,
  lenhSanXuatSearchColumns,
  mauSoHoaDonSearchColumns,
  phiSearchColumns,
  QUERY_KEYS,
  supplierSearchColumns,
  thueSearchColumns,
  tinhChatThueSearchColumns,
  vatTuSearchColumns,
  vuViecSearchColumns
} from '@/constants';
import {
  AccountModel,
  BoPhan,
  ChiPhi,
  DotThanhToan,
  HopDong,
  KhachHang,
  KheUoc,
  MauSoHoaDon,
  NhaCungCap,
  Phi,
  Tax,
  TinhChatThue,
  VatTu,
  VuViec
} from '@/types/schemas';
import { CellFieldV2 as CellField, SearchFieldV2 as SearchField } from '@/components/custom/erp';
import type { DataTableRenderCellParams } from '@/components/custom/erp/data-table-v2';
import { FormFieldActions, FormFieldState } from '../../../hooks';
import { FormMode } from '@/types/form';

export const getTaxTableColumns = ({
  formMode,
  state,
  actions,
  dien_giai,
  so_ct0,
  so_ct2,
  ngay_ct0,
  ma_kh,
  ten_kh,
  dia_chi,
  ma_so_thue,
  onCellValueChange,
  onCellCommit
}: {
  formMode: FormMode;
  state: FormFieldState;
  actions: FormFieldActions;
  dien_giai: string;
  so_ct0: string;
  so_ct2: string;
  ngay_ct0: string;
  ma_kh: string;
  ten_kh: string;
  dia_chi: string;
  ma_so_thue: string;
  onCellValueChange: (rowUuid: string, field: string, newValue: MuaHangRecord) => void;
  onCellCommit?: (rowUuid: string, field: string, newValue: MuaHangRecord) => void;
}) => [
  {
    field: 'so_ct0',
    headerName: 'Số hóa đơn',
    width: 100,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        commitOnBlur
        name='so_ct0'
        type='text'
        value={params.row.so_ct0 || so_ct0}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'so_ct0', newValue)}
        disabled={formMode === 'view'}
      />
    )
  },
  {
    field: 'so_ct2',
    headerName: 'Ký hiệu',
    width: 100,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        commitOnBlur
        name='so_ct2'
        type='text'
        value={params.row.so_ct2 || so_ct2}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'so_ct2', newValue)}
        disabled={formMode === 'view'}
      />
    )
  },
  {
    field: 'ngay_ct0',
    headerName: 'Ngày hóa đơn',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        commitOnBlur
        name='ngay_ct0'
        type='date'
        value={params.row.ngay_ct0 || ngay_ct0}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'ngay_ct0', newValue)}
        disabled={formMode === 'view'}
      />
    )
  },
  {
    field: 'ma_thue',
    headerName: 'Thuế suất',
    width: 170,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<Tax>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.THUE}/`}
        searchColumns={thueSearchColumns}
        columnDisplay='ten_thue'
        dialogTitle='Danh mục thuế'
        value={params.row.ma_thue_data?.ten_thue || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_thue_data', row)}
        className='w-full'
        disabled={formMode === 'view'}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_thue',
      resultField: 'ma_thue_data',
      detailEndpoint: QUERY_KEYS.THUE
    }
  },
  {
    field: 'ma_mau_ct',
    headerName: 'Mẫu hoá đơn',
    width: 250,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<MauSoHoaDon>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.MAU_SO_HOA_DON}/`}
        searchColumns={mauSoHoaDonSearchColumns}
        columnDisplay='ten_mau_so'
        dialogTitle='Danh mục mẫu hoá đơn'
        value={params.row.ma_mau_ct_data?.ten_mau_so || ''}
        onRowSelection={(row: MuaHangRecord) => {
          onCellValueChange(params.row.uuid, 'ma_mau_ct_data', row);
        }}
        className='w-full'
        disabled={formMode === 'view'}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_mau_ct',
      resultField: 'ma_mau_ct_data',
      detailEndpoint: QUERY_KEYS.MAU_SO_HOA_DON
    }
  },
  {
    field: 'ma_mau_bc',
    headerName: 'Mẫu báo cáo',
    width: 250,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        commitOnBlur
        name='ma_mau_bc'
        type='select'
        options={[
          { value: '3', label: '3. Hóa đơn giá trị gia tăng' },
          { value: '4', label: '4. Hàng hóa, dịch vụ mua vào không có hóa đơn' },
          { value: '5', label: '5. Hóa đơn bán hàng thông thường' }
        ]}
        value={params.row.ma_mau_bc || '3'}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'ma_mau_bc', newValue)}
        disabled={formMode === 'view'}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_mau_bc',
      resultField: 'ma_mau_bc_data',
      detailEndpoint: QUERY_KEYS.MAU_SO_HOA_DON
    }
  },
  {
    field: 'ma_tc_thue',
    headerName: 'Mã tính chất',
    width: 250,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<TinhChatThue>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.TINH_CHAT_THUE}/`}
        searchColumns={tinhChatThueSearchColumns}
        columnDisplay='ten_tc_thue'
        dialogTitle='Danh mục tính chất thuế'
        value={params.row.ma_tc_thue_data?.ten_tc_thue || ''}
        onRowSelection={(row: MuaHangRecord) => {
          onCellValueChange(params.row.uuid, 'ma_tc_thue_data', row);
        }}
        disabled={formMode === 'view'}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_tc_thue',
      resultField: 'ma_tc_thue_data',
      detailEndpoint: QUERY_KEYS.TINH_CHAT_THUE
    }
  },
  {
    field: 'ma_kh',
    headerName: 'Mã khách hàng',
    width: 170,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<NhaCungCap>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.NHA_CUNG_CAP}/`}
        searchColumns={supplierSearchColumns}
        columnDisplay='customer_code'
        dialogTitle='Danh mục nhà cung cấp'
        value={params.row.ma_kh_data?.customer_code || state.nhaCungCap?.customer_code || ''}
        onRowSelection={(row: MuaHangRecord) => {
          onCellValueChange(params.row.uuid, 'ma_kh_data', row);
        }}
        disabled={formMode === 'view'}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_kh',
      resultField: 'ma_kh_data',
      detailEndpoint: QUERY_KEYS.NHA_CUNG_CAP
    }
  },
  {
    field: 'ten_kh_thue',
    headerName: 'Tên nhà cung cấp',
    width: 200,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        commitOnBlur
        name='ten_kh_thue'
        type='text'
        value={params.row.ma_kh_data?.customer_name || ten_kh || state.nhaCungCap?.customer_name}
        disabled={formMode === 'view'}
      />
    )
  },
  {
    field: 'dia_chi',
    headerName: 'Địa chỉ',
    width: 200,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        commitOnBlur
        name='dia_chi'
        type='text'
        value={params.row.dia_chi || dia_chi || state.nhaCungCap?.address || params.row.ma_kh_data?.address}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'dia_chi', newValue)}
        disabled={formMode === 'view'}
      />
    )
  },
  {
    field: 'ma_so_thue',
    headerName: 'Mã số thuế',
    width: 200,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        commitOnBlur
        name='ma_so_thue'
        type='text'
        value={params.row.ma_so_thue || ma_so_thue || state.nhaCungCap?.tax_code || params.row.ma_kh_data?.tax_code}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'ma_so_thue', newValue)}
        disabled={formMode === 'view'}
      />
    )
  },
  {
    field: 'ten_vt_thue',
    headerName: 'Tên hàng hóa - dịch vụ',
    width: 300,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        commitOnBlur
        name='ten_vt_thue'
        type='text'
        value={params.row.ten_vt_thue || dien_giai}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'ten_vt_thue', newValue)}
        disabled={formMode === 'view'}
      />
    )
  },
  {
    field: 't_tien_nt',
    headerName: 'Tiền hàng VND',
    width: 200,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        commitOnBlur
        name='t_tien_nt'
        type='number'
        value={params.row.t_tien_nt}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 't_tien_nt', newValue)}
        disabled={formMode === 'view'}
      />
    )
  },
  {
    field: 'tk_thue_no',
    headerName: 'Tk thuế',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<AccountModel>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
        searchColumns={accountSearchColumns}
        columnDisplay='code'
        dialogTitle='Danh mục tài khoản'
        value={params.row.tk_thue_no_data?.code || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'tk_thue_no_data', row)}
        className='w-full'
        disabled={formMode === 'view'}
      />
    ),
    pasteConfig: {
      lookupKey: 'tk_thue_no',
      resultField: 'tk_thue_no_data',
      detailEndpoint: QUERY_KEYS.TAI_KHOAN
    }
  },
  {
    field: 't_thue_nt',
    headerName: 'Thuế VND',
    width: 200,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        commitOnBlur
        name='t_thue_nt'
        type='number'
        value={params.row.t_thue_nt}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 't_thue_nt', newValue)}
        onCommit={newValue => onCellCommit?.(params.row.uuid, 't_thue_nt', newValue)}
        disabled={formMode === 'view'}
      />
    )
  },
  {
    field: 'ma_kh9',
    headerName: 'Cục thuế',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<KhachHang>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.KHACH_HANG}/`}
        searchColumns={khachHangSearchColumns}
        columnDisplay='customer_code'
        dialogTitle='Danh mục đối tượng'
        value={params.row.ma_kh9_data?.customer_code || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_kh9_data', row)}
        className='w-full'
        disabled={formMode === 'view'}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_tt',
      resultField: 'ma_tt_data',
      detailEndpoint: QUERY_KEYS.DOT_THANH_TOAN
    }
  },

  {
    field: 'ma_tt',
    headerName: 'Mã thanh toán',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<DotThanhToan>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.DOT_THANH_TOAN}/`}
        searchColumns={dotThanhToanSearchColumns}
        columnDisplay='ma_dtt'
        dialogTitle='Danh mục thanh toán'
        value={params.row.ma_tt_data?.ma_dtt}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_tt_data', row)}
        disabled={formMode === 'view'}
      />
    ),
    pasteConfig: {
      lookupKey: 'ghi_chu',
      resultField: 'ghi_chu_data',
      detailEndpoint: QUERY_KEYS.DOT_THANH_TOAN
    }
  },
  {
    field: 'ghi_chu',
    headerName: 'Ghi chú',
    width: 300,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        commitOnBlur
        name='ghi_chu'
        type='text'
        value={params.row.ghi_chu}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'ghi_chu', newValue)}
        disabled={formMode === 'view'}
      />
    )
  },
  {
    field: 'ma_bp',
    headerName: 'Bộ phận',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<BoPhan>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.BO_PHAN}/`}
        searchColumns={boPhanSearchColumns}
        columnDisplay='ma_bp'
        dialogTitle='Danh mục bộ phận'
        value={params.row.ma_bp_data?.ma_bp || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_bp_data', row)}
        disabled={formMode === 'view'}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_bp',
      resultField: 'ma_bp_data',
      detailEndpoint: QUERY_KEYS.BO_PHAN
    }
  },
  {
    field: 'ma_vv',
    headerName: 'Vụ việc',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<VuViec>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.VU_VIEC}/`}
        searchColumns={vuViecSearchColumns}
        columnDisplay='ma_vu_viec'
        dialogTitle='Danh mục vụ việc'
        value={params.row.ma_vv_data?.ma_vu_viec || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_vv_data', row)}
        disabled={formMode === 'view'}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_vv',
      resultField: 'ma_vv_data',
      detailEndpoint: QUERY_KEYS.VU_VIEC
    }
  },
  {
    field: 'ma_hd',
    headerName: 'Hợp đồng',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<HopDong>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.HOP_DONG}/`}
        searchColumns={hopDongSearchColumns}
        columnDisplay='ma_hd'
        dialogTitle='Danh mục hợp đồng'
        value={params.row.ma_hd_data?.ma_hd || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_hd_data', row)}
        disabled={formMode === 'view'}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_hd',
      resultField: 'ma_hd_data',
      detailEndpoint: QUERY_KEYS.HOP_DONG
    }
  },
  {
    field: 'ma_dtt',
    headerName: 'Đợt thanh toán',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<DotThanhToan>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.DOT_THANH_TOAN}/`}
        searchColumns={dotThanhToanSearchColumns}
        columnDisplay='ma_dtt'
        dialogTitle='Danh mục đợt thanh toán'
        value={params.row.ma_dtt_data?.ma_dtt || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_dtt_data', row)}
        disabled={formMode === 'view'}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_dtt',
      resultField: 'ma_dtt_data',
      detailEndpoint: QUERY_KEYS.DOT_THANH_TOAN
    }
  },
  {
    field: 'ma_ku',
    headerName: 'Khế ước',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<KheUoc>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.KHE_UOC}/`}
        searchColumns={kheUocSearchColumns}
        columnDisplay='ma_ku'
        dialogTitle='Danh mục khế ước'
        value={params.row.ma_ku_data?.ma_ku || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_ku_data', row)}
        disabled={formMode === 'view'}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_ku',
      resultField: 'ma_ku_data',
      detailEndpoint: QUERY_KEYS.KHE_UOC
    }
  },
  {
    field: 'ma_phi',
    headerName: 'Phí',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<Phi>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.PHI}/`}
        searchColumns={phiSearchColumns}
        columnDisplay='ma_phi'
        dialogTitle='Danh mục phí'
        value={params.row.ma_phi_data?.ma_phi || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_phi_data', row)}
        disabled={formMode === 'view'}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_phi',
      resultField: 'ma_phi_data',
      detailEndpoint: QUERY_KEYS.PHI
    }
  },
  {
    field: 'ma_sp',
    headerName: 'Sản phẩm',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<VatTu>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.VAT_TU}/`}
        searchColumns={vatTuSearchColumns}
        columnDisplay='ma_vt'
        dialogTitle='Danh mục sản phẩm'
        value={params.row.ma_sp_data?.ma_vt || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_sp_data', row)}
        className='w-full'
        disabled={formMode === 'view'}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_sp',
      resultField: 'ma_sp_data',
      detailEndpoint: QUERY_KEYS.VAT_TU
    }
  },
  {
    field: 'ma_lsx',
    headerName: 'Lệnh sản xuất',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<MuaHangRecord>
        type='text'
        searchEndpoint={'/'}
        searchColumns={lenhSanXuatSearchColumns}
        columnDisplay='ma_lsx'
        dialogTitle='Danh mục lệnh sản xuất'
        value={params.row.ma_lsx_data?.ma_lsx || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_lsx_data', row)}
        className='w-full'
        disabled={formMode === 'view'}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_lsx',
      resultField: 'ma_lsx_data',
      detailEndpoint: QUERY_KEYS.LENH_SAN_XUAT
    }
  },
  {
    field: 'ma_cp0',
    headerName: 'C/p không h/lệ',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<ChiPhi>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.CHI_PHI}/`}
        searchColumns={chiPhiSearchColumns}
        columnDisplay='ma_cp'
        dialogTitle='Danh mục chi phí không hợp lệ'
        value={params.row.ma_cp0_data?.ma_cp || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_cp0_data', row)}
        className='w-full'
        disabled={formMode === 'view'}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_cp0',
      resultField: 'ma_cp0_data',
      detailEndpoint: QUERY_KEYS.CHI_PHI
    }
  }
];
